import { Component, inject, signal, computed, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { EntityFormComponent } from '../../components/entity-form/entity-form.component';
import { EntitiesListComponent } from '../../components/entities-list/entities-list.component';
import { EntityService } from '../../services/entity.service';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-entity-management',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        EntityFormComponent,
        EntitiesListComponent,
        NgSelectModule,
        PageHeaderComponent,
        MobileFooterComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './entity-management.component.html',
    styleUrl: './entity-management.component.scss'
})
export class EntityManagementComponent {
    public catalogService = inject(CatalogService);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);
    mobileSearch = '';

    @ViewChild('entityModal') entityModal!: TemplateRef<any>;
    @ViewChild(EntityFormComponent) entityFormCmp?: EntityFormComponent;

    selectedEntity: any = null;
    selectedId = signal<number | null>(null);
    searchTerm = signal('');
    entityForm: FormGroup;
    mode = signal<FormMode>(FormMode.VIEW);
    formMode = FormMode;
    showDetail = false;

    private fb = inject(FormBuilder);
    private entityService = inject(EntityService);
    private modalService = inject(NgbModal);

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    get showMobileDetail(): boolean {
        return this.appSettings.isMobile() && this.showDetail;
    }

    searchItems = computed(() => {
        return this.catalogService.entities().map((e) => ({
            ...e,
            label: e.isActive === false
                ? `${e.code} - ${e.name} (inactiva)`
                : (e.label || `${e.code} - ${e.name}`)
        }));
    });

    filteredEntities = computed(() => {
        const all = this.catalogService.entities();
        const selectedId = this.selectedId();
        if (selectedId != null) {
            return all.filter((e) => Number(e.id) === Number(selectedId));
        }
        const q = this.searchTerm().trim().toLowerCase();
        if (!q) return all;
        return all.filter((e) => {
            const haystack = [e.code, e.name, e.campus, e.mail].map((v) => String(v || '').toLowerCase()).join(' ');
            return haystack.includes(q);
        });
    });

    constructor() {
        this.entityForm = this.fb.group({
            id: [null],
            code: [null, [Validators.required]],
            name: [null, [Validators.required]],
            group: [null],
            campus: [null],
            mail: [null, [Validators.email]],
            cc: [null],
            isActive: [true]
        });
    }

    onSearchTerm(event: { term?: string } | string): void {
        const term = typeof event === 'string' ? event : (event?.term || '');
        this.searchTerm.set(term);
        if (term) this.selectedId.set(null);
    }

    onSearchSelect(id: number | null): void {
        this.selectedId.set(id ?? null);
        if (id != null) this.searchTerm.set('');
    }

    openEdit(entity: any): void {
        this.openEditor(entity, FormMode.EDIT);
    }

    createNewEntity(): void {
        this.openEditor({ isNew: true }, FormMode.CREATE);
    }

    closeDetail(): void {
        this.showDetail = false;
        this.selectedEntity = null;
        this.mode.set(FormMode.VIEW);
        this.modalService.dismissAll();
    }

    private openEditor(entity: any, mode: FormMode): void {
        this.selectedEntity = entity;
        this.mode.set(mode);
        this.entityForm.reset(mode === FormMode.CREATE
            ? { isActive: true }
            : { ...entity, isActive: entity.isActive !== false });
        if (this.appSettings.isMobile()) {
            this.showDetail = true;
            return;
        }
        this.modalService.open(this.entityModal, { backdrop: 'static', size: 'lg', centered: true });
    }

    saveFromModal(): void {
        this.entityFormCmp?.commitCcDraft();
        if (this.entityForm.invalid) return;
        this.handleFormChange(this.entityForm.value);
    }

    handleFormChange(formValue: any): void {
        if (formValue === null) {
            this.closeDetail();
            this.catalogService.reloadEntities();
            return;
        }

        if (this.mode() === FormMode.EDIT) {
            this.updateEntity(formValue);
        } else if (this.mode() === FormMode.CREATE) {
            this.createEntity(formValue);
        }
    }

    private updateEntity(data: any): void {
        if (!this.selectedEntity?.id && !data?.id) return;
        this.entityService.update({ ...data, id: data.id || this.selectedEntity.id }).subscribe({
            next: () => {
                this.Toast.fire({ icon: 'success', title: 'Entidad actualizada correctamente' });
                this.catalogService.reloadEntities();
                this.closeDetail();
            },
            error: () => Swal.fire('Error', 'No se pudo actualizar la entidad', 'error')
        });
    }

    private createEntity(data: any): void {
        this.entityService.create(data).subscribe({
            next: () => {
                this.Toast.fire({ icon: 'success', title: 'Entidad creada correctamente' });
                this.catalogService.reloadEntities();
                this.closeDetail();
            },
            error: () => Swal.fire('Error', 'No se pudo crear la entidad', 'error')
        });
    }
}
